import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TIME_GRAINS, type AnalysisState, type TimeGrain } from '@aibi/contracts';
import { cn, IconButton, Select } from '@aibi/ui';
import { GripVertical, X } from 'lucide-react';
import type { FieldKind } from '../../model/field-catalog';
import type { DragSource, DropTarget } from '../../model/drop-rules';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';
import { DropZone } from '../dnd/DropZone';
import { dropFeedbackClass, useDropValidity } from '../dnd/drag-state';
import { FieldIcon } from '../field-explorer/FieldIcon';

const GRAIN_LABELS: Readonly<Record<TimeGrain, string>> = {
  year: 'Ano',
  quarter: 'Trimestre',
  month: 'Mes',
  week: 'Semana',
  day: 'Dia',
  hour: 'Hora',
};

interface SlotItem {
  readonly key: string;
  readonly fieldId: string;
  readonly timeGrain?: TimeGrain;
}

/**
 * Lista ordenable de dimensiones o medidas de la visualizacion. Acepta campos del
 * explorador (solo del tipo correcto) y reordenar arrastrando o con el teclado.
 */
export function FieldSlotList({
  card,
  kind,
}: {
  readonly card: AnalysisState;
  readonly kind: FieldKind;
}) {
  const items: SlotItem[] =
    kind === 'dimension'
      ? card.dimensions.map((d) => ({
          key: d.key,
          fieldId: d.dimensionId,
          ...(d.timeGrain === undefined ? {} : { timeGrain: d.timeGrain }),
        }))
      : card.measures.map((m) => ({ key: m.key, fieldId: m.measureId }));
  const title = kind === 'dimension' ? 'Dimensiones' : 'Medidas';

  return (
    <DropZone
      id={`slot:${card.id}:${kind}`}
      target={{ type: 'slot', cardId: card.id, kind }}
      label={`Zona de ${title.toLowerCase()}`}
      className="p-0.5"
    >
      <SortableContext
        items={items.map((item) => `slot:${card.id}:${item.key}`)}
        strategy={verticalListSortingStrategy}
      >
        {items.length === 0 ? (
          <p className="rounded-md border border-dashed border-border px-2 py-2 text-xs text-muted-foreground">
            Arrastre {kind === 'dimension' ? 'una dimension' : 'una medida'} aqui.
          </p>
        ) : (
          <ul aria-label={title} className="space-y-1">
            {items.map((item, index) => (
              <SlotRow key={item.key} card={card} kind={kind} item={item} index={index} />
            ))}
          </ul>
        )}
      </SortableContext>
    </DropZone>
  );
}

function SlotRow({
  card,
  kind,
  item,
  index,
}: {
  readonly card: AnalysisState;
  readonly kind: FieldKind;
  readonly item: SlotItem;
  readonly index: number;
}) {
  const { catalog } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const field = catalog.get(item.fieldId);
  const source: DragSource = { type: 'slot-field', cardId: card.id, kind, index };
  const target: DropTarget = { type: 'slot', cardId: card.id, kind, index };
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: `slot:${card.id}:${item.key}`, data: { source, target } });
  const valid = useDropValidity(target);
  const name = field?.name ?? item.fieldId;

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'flex h-8 items-center gap-1 rounded-md border border-border bg-card pr-1 text-xs',
        isDragging && 'opacity-50',
        dropFeedbackClass(valid, isOver),
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Reordenar ${name}`}
        className="flex h-full w-6 cursor-grab items-center justify-center rounded-l-md text-muted-foreground outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
      >
        <GripVertical aria-hidden className="size-3" />
      </button>
      {field && <FieldIcon role={field.role} />}
      <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
      {field?.temporal === true && (
        <Select
          aria-label={`Granularidad de ${name}`}
          value={item.timeGrain ?? ''}
          className="h-6 w-24 px-1 text-xs"
          onChange={(event) => {
            const value = event.target.value;
            execute({
              type: 'set-time-grain',
              cardId: card.id,
              key: item.key,
              grain: value === '' ? null : (value as TimeGrain),
            });
          }}
        >
          <option value="">Sin agrupar</option>
          {TIME_GRAINS.map((grain) => (
            <option key={grain} value={grain}>
              {GRAIN_LABELS[grain]}
            </option>
          ))}
        </Select>
      )}
      <IconButton
        label={`Quitar ${name}`}
        className="size-6"
        onClick={() => {
          execute({ type: 'remove-field', cardId: card.id, key: item.key });
        }}
      >
        <X />
      </IconButton>
    </li>
  );
}
